import React, { useEffect, useRef, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getPublicSiteFs, savePublicSiteFs } from '../../utils/firestoreStore';
import { faculties as defaultFaculties } from '../../publicSite/facultyData';
import { normalizeFacultiesContent } from '../../publicSite/facultyContent';
import { storage } from '../../firebase';

const blankProduct = () => ({
  id: `product-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  type: 'book',
  description: '',
  image: '',
  price: 0,
  format: 'physical',
  pdfUrl: '',
  pdfFileName: '',
  videoUrl: '',
  videoFileName: ''
});

export default function AdminFaculties() {
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [faculties, setFaculties] = useState(() => normalizeFacultiesContent(defaultFaculties));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [uploadingKey, setUploadingKey] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadAccept, setUploadAccept] = useState('image/*,.pdf,application/pdf,video/*');
  const fileInputRef = useRef(null);
  const pendingUploadRef = useRef(null);

  useEffect(() => {
    if (!useFirebase) return;
    getPublicSiteFs()
      .then((data) => {
        const next = normalizeFacultiesContent(data?.facultiesContent || []);
        setFaculties(next);
      })
      .catch(() => {});
  }, [useFirebase]);

  const updateFaculty = (slug, updater) => {
    setFaculties((prev) =>
      prev.map((faculty) => (faculty.slug === slug ? updater(faculty) : faculty))
    );
  };

  const updateHighlights = (slug, value) => {
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    updateFaculty(slug, (faculty) => ({
      ...faculty,
      details: {
        ...faculty.details,
        highlights: lines
      }
    }));
  };

  const addProduct = (slug) => {
    updateFaculty(slug, (faculty) => ({
      ...faculty,
      products: [...(faculty.products || []), blankProduct()]
    }));
  };

  const removeProduct = (slug, productId) => {
    updateFaculty(slug, (faculty) => ({
      ...faculty,
      products: (faculty.products || []).filter((product) => product.id !== productId)
    }));
  };

  const updateProduct = (slug, productId, updater) => {
    updateFaculty(slug, (faculty) => ({
      ...faculty,
      products: (faculty.products || []).map((product) =>
        product.id === productId ? updater(product) : product
      )
    }));
  };

  const startImageUpload = (target) => {
    if (!useFirebase) return;
    if (target.kind === 'productPdf') {
      setUploadAccept('.pdf,application/pdf');
    } else if (target.kind === 'productVideo') {
      setUploadAccept('video/*');
    } else {
      setUploadAccept('image/*');
    }
    pendingUploadRef.current = target;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    const target = pendingUploadRef.current;
    event.target.value = '';
    if (!file || !target || !useFirebase) return;

    setUploadingKey(target.key);
    setUploadProgress(0);
    if (target.kind === 'productPdf') {
      setStatus('Uploading PDF...');
    } else if (target.kind === 'productVideo') {
      setStatus('Uploading video...');
    } else {
      setStatus('Uploading image...');
    }
    try {
      const storagePath = `public/faculties/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed', (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setUploadProgress(progress);
      });
      await uploadTask;
      const downloadUrl = await getDownloadURL(storageRef);

      if (target.kind === 'facultyLogo') {
        updateFaculty(target.slug, (faculty) => ({ ...faculty, logo: downloadUrl }));
      }
      if (target.kind === 'productImage') {
        updateProduct(target.slug, target.productId, (product) => ({ ...product, image: downloadUrl }));
      }
      if (target.kind === 'productPdf') {
        updateProduct(target.slug, target.productId, (product) => ({
          ...product,
          pdfUrl: downloadUrl,
          pdfFileName: file.name
        }));
      }
      if (target.kind === 'productVideo') {
        updateProduct(target.slug, target.productId, (product) => ({
          ...product,
          videoUrl: downloadUrl,
          videoFileName: file.name
        }));
      }

      if (target.kind === 'productPdf') {
        setStatus('PDF uploaded.');
      } else if (target.kind === 'productVideo') {
        setStatus('Video uploaded.');
      } else {
        setStatus('Image uploaded.');
      }
      setTimeout(() => setStatus(''), 2500);
    } catch {
      setStatus('File upload failed.');
      setTimeout(() => setStatus(''), 2500);
    } finally {
      setUploadingKey('');
      setUploadProgress(0);
      setUploadAccept('image/*,.pdf,application/pdf,video/*');
      pendingUploadRef.current = null;
    }
  };

  const handleSave = async () => {
    if (!useFirebase) {
      setStatus('Firebase is disabled; unable to save faculties.');
      setTimeout(() => setStatus(''), 3000);
      return;
    }
    setSaving(true);
    setStatus('Saving faculty pages...');
    try {
      await savePublicSiteFs({ facultiesContent: faculties });
      setStatus('Faculty pages updated successfully.');
      setTimeout(() => setStatus(''), 3000);
    } catch {
      setStatus('Unable to save faculty pages.');
      setTimeout(() => setStatus(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Faculty Pages & Store</h1>
        <p className="page-subtitle">Edit faculty page content and add books/courses for sale on each faculty page.</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={uploadAccept}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {status && (
        <div className="badge badge-info" style={{ marginBottom: '16px' }}>
          {uploadingKey ? `${status} (${uploadProgress}%)` : status}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 style={{ fontSize: '1.125rem' }}>Edit Faculties</h3>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '16px' }}>
          {faculties.map((faculty) => (
            <div
              key={faculty.slug}
              style={{
                display: 'grid',
                gap: '10px',
                border: '1px solid var(--gray-100)',
                background: 'var(--gray-50)',
                borderRadius: '12px',
                padding: '12px'
              }}
            >
              <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Slug: {faculty.slug}</div>
              <input
                className="form-input"
                placeholder="Title"
                value={faculty.title}
                onChange={(e) => updateFaculty(faculty.slug, (item) => ({ ...item, title: e.target.value }))}
              />
              <textarea
                className="form-input form-textarea"
                placeholder="Summary"
                value={faculty.summary}
                onChange={(e) => updateFaculty(faculty.slug, (item) => ({ ...item, summary: e.target.value }))}
              />
              <input
                className="form-input"
                placeholder="Logo path or URL"
                value={faculty.logo}
                onChange={(e) => updateFaculty(faculty.slug, (item) => ({ ...item, logo: e.target.value }))}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => startImageUpload({ kind: 'facultyLogo', slug: faculty.slug, key: `faculty-logo-${faculty.slug}` })}
                disabled={Boolean(uploadingKey) || saving}
              >
                {uploadingKey === `faculty-logo-${faculty.slug}` ? `Uploading… ${uploadProgress}%` : 'Upload Faculty Logo'}
              </button>
              <textarea
                className="form-input form-textarea"
                placeholder="Overview"
                value={faculty.details?.overview || ''}
                onChange={(e) =>
                  updateFaculty(faculty.slug, (item) => ({
                    ...item,
                    details: { ...item.details, overview: e.target.value }
                  }))
                }
              />
              <textarea
                className="form-input form-textarea"
                placeholder="Highlights (one point per line)"
                value={(faculty.details?.highlights || []).join('\n')}
                onChange={(e) => updateHighlights(faculty.slug, e.target.value)}
              />
              <textarea
                className="form-input form-textarea"
                placeholder="Outcome"
                value={faculty.details?.outcome || ''}
                onChange={(e) =>
                  updateFaculty(faculty.slug, (item) => ({
                    ...item,
                    details: { ...item.details, outcome: e.target.value }
                  }))
                }
              />

              <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '10px', display: 'grid', gap: '8px' }}>
                <div style={{ fontWeight: 700 }}>Products for this faculty</div>
                {(faculty.products || []).length === 0 && (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>No products yet.</div>
                )}
                {(faculty.products || []).map((product) => (
                  <div key={product.id} style={{ border: '1px solid var(--gray-200)', background: '#fff', borderRadius: '10px', padding: '10px', display: 'grid', gap: '8px' }}>
                    <input
                      className="form-input"
                      placeholder="Product title"
                      value={product.title}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, title: e.target.value }))}
                    />
                    <select
                      className="form-input"
                      value={product.type}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, type: e.target.value }))}
                    >
                      <option value="book">Book</option>
                      <option value="course">Course</option>
                      <option value="material">Material</option>
                    </select>
                    <textarea
                      className="form-input form-textarea"
                      placeholder="Description"
                      value={product.description}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, description: e.target.value }))}
                    />
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Price (ZAR)"
                      value={product.price}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, price: Number(e.target.value) || 0 }))}
                    />
                    <select
                      className="form-input"
                      value={product.format || 'physical'}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, format: e.target.value }))}
                    >
                      <option value="physical">Physical</option>
                      <option value="pdf">PDF</option>
                      <option value="both">Both (choose at checkout)</option>
                    </select>
                    <input
                      className="form-input"
                      placeholder="Image URL"
                      value={product.image}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, image: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        startImageUpload({
                          kind: 'productImage',
                          slug: faculty.slug,
                          productId: product.id,
                          key: `product-image-${faculty.slug}-${product.id}`
                        })
                      }
                        disabled={Boolean(uploadingKey) || saving}
                    >
                      {uploadingKey === `product-image-${faculty.slug}-${product.id}` ? `Uploading… ${uploadProgress}%` : 'Upload Product Image'}
                    </button>
                    <input
                      className="form-input"
                      placeholder="PDF URL (optional)"
                      value={product.pdfUrl || ''}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, pdfUrl: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        startImageUpload({
                          kind: 'productPdf',
                          slug: faculty.slug,
                          productId: product.id,
                          key: `product-pdf-${faculty.slug}-${product.id}`
                        })
                      }
                      disabled={Boolean(uploadingKey) || saving}
                    >
                      {uploadingKey === `product-pdf-${faculty.slug}-${product.id}` ? `Uploading… ${uploadProgress}%` : 'Upload PDF File'}
                    </button>
                    {product.pdfFileName && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>PDF: {product.pdfFileName}</div>
                    )}
                    <input
                      className="form-input"
                      placeholder="Video URL or YouTube embed link (optional)"
                      value={product.videoUrl || ''}
                      onChange={(e) => updateProduct(faculty.slug, product.id, (item) => ({ ...item, videoUrl: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() =>
                        startImageUpload({
                          kind: 'productVideo',
                          slug: faculty.slug,
                          productId: product.id,
                          key: `product-video-${faculty.slug}-${product.id}`
                        })
                      }
                      disabled={Boolean(uploadingKey) || saving}
                    >
                      {uploadingKey === `product-video-${faculty.slug}-${product.id}` ? `Uploading… ${uploadProgress}%` : 'Upload Product Video'}
                    </button>
                    {product.videoFileName && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Video: {product.videoFileName}</div>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => removeProduct(faculty.slug, product.id)}
                    >
                      Remove Product
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => addProduct(faculty.slug)}>
                  Add Product
                </button>
              </div>
            </div>
          ))}

          <div>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Faculty Pages'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
